import { ConnectionsPage } from './components/connections/ConnectionsPage';
import { EmailWorkspace } from './components/email/EmailWorkspace';
import {
  createRootRoute, createRoute, createRouter, Navigate, Outlet, redirect,
} from '@tanstack/react-router';
import { AppShell } from './components/shell/AppShell';
import { ChatView } from './components/chat/ChatView';
import { ObjectIndex } from './components/crm/ObjectIndex';
import { RecordDetail } from './components/crm/RecordDetail';
import { ActivityPage } from './routes/app/activity';
import { BriefPage } from './routes/app/brief';
import { NewChatPage, NewConversationPage } from './routes/app/new-chat';
import {
  AiMemorySettingsPage,
  AiProviderSettingsPage,
  BillingUsageSettingsPage,
  GoalsSettingsPage,
  GmailSettingsPage,
  SettingsHomePage,
  SettingsLayout,
  ThemeSettingsPage,
} from './routes/app/settings';
import { AuthCallbackPage } from './routes/public/auth-callback';
import { SignInPage } from './routes/public/sign-in';
import { AboutPage } from './marketing/About';
import { LandingPage } from './marketing/Landing';
import { PricingPage } from './marketing/Pricing';
import { loadSession } from './lib/session';
import { useAuth } from './lib/auth';

const rootRoute = createRootRoute({ component: Outlet });

/* ---------- Public / marketing ---------- */

const landingRoute = createRoute({ getParentRoute: () => rootRoute, path: '/', component: LandingPage });
const pricingRoute = createRoute({ getParentRoute: () => rootRoute, path: '/pricing', component: PricingPage });
const aboutRoute = createRoute({ getParentRoute: () => rootRoute, path: '/about', component: AboutPage });
const signInRoute = createRoute({ getParentRoute: () => rootRoute, path: '/sign-in', component: SignInPage });
const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/callback',
  component: AuthCallbackPage,
});

/* ---------- Authenticated app ---------- */

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app',
  beforeLoad: ({ location }) => {
    if (!loadSession()) {
      throw redirect({ to: '/sign-in', search: { redirect: location.href } });
    }
  },
  component: AuthenticatedApp,
});

function AuthenticatedApp() {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/sign-in" />;
  return <AppShell><Outlet /></AppShell>;
}

const newChatRoute = createRoute({ getParentRoute: () => appRoute, path: '/', component: NewChatPage });
const newConversationRoute = createRoute({ getParentRoute: () => appRoute, path: '/new', component: NewConversationPage });

const conversationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/c/$conversationId',
  component: function ConversationRoute() {
    const { conversationId } = conversationRoute.useParams();
    return <ChatView conversationId={conversationId} />;
  },
});

const objectIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/o/$object',
  component: function ObjectIndexRoute() {
    const { object } = objectIndexRoute.useParams();
    return <ObjectIndex objectName={object} />;
  },
});

const recordDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/o/$object/$recordId',
  component: function RecordDetailRoute() {
    const { object, recordId } = recordDetailRoute.useParams();
    return <RecordDetail objectName={object} recordId={recordId} />;
  },
});

const briefRoute = createRoute({ getParentRoute: () => appRoute, path: '/brief', component: BriefPage });
const emailHomeRoute = createRoute({getParentRoute:()=>appRoute,path:'/email',component:()=> <Navigate to="/app/email/automations" replace/>});
const emailTemplatesRoute = createRoute({getParentRoute:()=>appRoute,path:'/email/templates',component:()=> <EmailWorkspace kind="templates"/>});
const emailTemplateRoute = createRoute({getParentRoute:()=>appRoute,path:'/email/templates/$resourceId',component:()=> <EmailWorkspace kind="templates" detail/>});
const emailAutomationsRoute = createRoute({getParentRoute:()=>appRoute,path:'/email/automations',component:()=> <EmailWorkspace kind="automations"/>});
const emailAutomationCreateRoute = createRoute({getParentRoute:()=>appRoute,path:'/email/automations/new',component:()=> <EmailWorkspace kind="automations" create/>});
const emailAutomationRoute = createRoute({getParentRoute:()=>appRoute,path:'/email/automations/$resourceId',component:()=> <EmailWorkspace kind="automations" detail/>});
const emailRunRoute = createRoute({getParentRoute:()=>appRoute,path:'/email/enrollments/$enrollmentId',component:()=> <EmailWorkspace kind="automations" run/>});
const connectionsRoute=createRoute({getParentRoute:()=>appRoute,path:'/connections',component:()=> <ConnectionsPage/>});
const connectionRoute=createRoute({getParentRoute:()=>appRoute,path:'/connections/$connectionId',component:function ConnectionRoute(){const {connectionId}=connectionRoute.useParams();return <ConnectionsPage connectionId={connectionId}/>;}});
const activityRoute = createRoute({ getParentRoute: () => appRoute, path: '/activity', component: ActivityPage });
const settingsRoute = createRoute({ getParentRoute: () => appRoute, path: '/settings', component: SettingsLayout });
const settingsHomeRoute = createRoute({ getParentRoute: () => settingsRoute, path: '/', component: SettingsHomePage });
const themeSettingsRoute = createRoute({ getParentRoute: () => settingsRoute, path: '/theme', component: ThemeSettingsPage });
const aiMemorySettingsRoute = createRoute({ getParentRoute: () => settingsRoute, path: '/ai-memory', component: AiMemorySettingsPage });
const goalsSettingsRoute = createRoute({ getParentRoute: () => settingsRoute, path: '/goals', component: GoalsSettingsPage });
const aiProviderSettingsRoute = createRoute({ getParentRoute: () => settingsRoute, path: '/ai-provider', component: AiProviderSettingsPage });
const gmailSettingsRoute = createRoute({ getParentRoute: () => settingsRoute, path: '/gmail', component: GmailSettingsPage });
const billingUsageSettingsRoute = createRoute({ getParentRoute: () => settingsRoute, path: '/billing-usage', component: BillingUsageSettingsPage });

const notFoundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '$',
  component: () => <Navigate to="/" />,
});

const routeTree = rootRoute.addChildren([
  landingRoute,
  pricingRoute,
  aboutRoute,
  signInRoute,
  authCallbackRoute,
  appRoute.addChildren([
    newChatRoute,
    newConversationRoute,
    conversationRoute,
    objectIndexRoute,
    recordDetailRoute,
    briefRoute,
    emailHomeRoute, emailTemplatesRoute, emailTemplateRoute, emailAutomationsRoute, emailAutomationCreateRoute, emailAutomationRoute, emailRunRoute,
    connectionsRoute, connectionRoute,
    activityRoute,
    settingsRoute.addChildren([
      settingsHomeRoute,
      themeSettingsRoute,
      aiMemorySettingsRoute,
      goalsSettingsRoute,
      aiProviderSettingsRoute,
      gmailSettingsRoute,
      billingUsageSettingsRoute,
    ]),
  ]),
  notFoundRoute,
]);

export const router = createRouter({
  routeTree,
  // Prefetch on hover so a click lands on an already-warm route — the spec's
  // sub-100ms transition budget.
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
